// src/components/admin/UserManagement.js
import React, { useState, useEffect } from 'react';
import logger from '../../utils/logger';
import { Routes, Route, Link } from 'react-router-dom';
import {
  Users, PlusCircle, Search,
  MoreHorizontal,
  RefreshCw, Edit, Trash2, UserPlus, XCircle,
  CheckCircle, UserCog, AlertTriangle, Shield
} from 'lucide-react';

/**
 * UserManagement Component - Manage portal users
 * Includes listing, creation, editing, and activation functionality
 */
const UserManagement = ({ bcService }) => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showActionMenu, setShowActionMenu] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  
  // Fetch users on component mount
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // Fetch users from BC
        const response = await bcService.getPortalUsers();
        setUsers(response);
        setFilteredUsers(response);
      } catch (error) {
        logger.error('Error fetching users:', error);
        setError('Failed to load users. Please try again.');
        
        // Mock data for development
        if (process.env.NODE_ENV === 'development') {
          const mockUsers = [
            { 
              id: '1', 
              name: 'John Smith', 
              email: 'john.smith@example.com', 
              status: 'Active',
              role: 'User',
              lastLogin: '2025-05-05T12:30:00Z',
              created: '2025-01-15T09:00:00Z'
            },
            { 
              id: '2', 
              name: 'Emma Thompson', 
              email: 'emma.thompson@example.com', 
              status: 'Active',
              role: 'User',
              lastLogin: '2025-05-04T15:45:00Z',
              created: '2025-01-20T10:15:00Z'
            },
            { 
              id: '3', 
              name: 'Michael Johnson', 
              email: 'michael.johnson@example.com', 
              status: 'PendingFirstLogin',
              role: 'User',
              lastLogin: null,
              created: '2025-05-01T11:30:00Z'
            },
            { 
              id: '4', 
              name: 'Sarah Williams', 
              email: 'sarah.williams@example.com', 
              status: 'Inactive',
              role: 'User',
              lastLogin: '2025-03-15T14:20:00Z',
              created: '2025-01-25T08:45:00Z'
            },
            { 
              id: '5', 
              name: 'Admin User', 
              email: 'admin@example.com', 
              status: 'Active',
              role: 'Admin',
              isAdmin: true,
              lastLogin: '2025-05-08T09:10:00Z',
              created: '2025-01-10T08:00:00Z'
            }
          ];
          
          setUsers(mockUsers);
          setFilteredUsers(mockUsers);
        }
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchUsers();
  }, [bcService]);
  
  // Filter users based on search term, tab, and status filter
  useEffect(() => {
    let result = [...users];

    // Apply tab filter
    if (activeTab === 'active') {
      result = result.filter(user => user.status === 'Active');
    } else if (activeTab === 'inactive') {
      result = result.filter(user => user.status === 'Inactive' || user.status === 'PendingFirstLogin');
    } else if (activeTab === 'admin') {
      result = result.filter(user => user.isAdmin);
    }

    // Apply search term filter
    if (searchTerm) {
      const lowerCaseSearchTerm = searchTerm.toLowerCase();
      result = result.filter(
        user =>
          user.name.toLowerCase().includes(lowerCaseSearchTerm) ||
          user.email.toLowerCase().includes(lowerCaseSearchTerm)
      );
    }

    setFilteredUsers(result);
  }, [searchTerm, activeTab, users]);
  
  // Toggle action menu for a user
  const toggleActionMenu = (userId) => {
    setShowActionMenu(showActionMenu === userId ? null : userId);
  };
  
  // Handle user activation
  const handleActivateUser = async (userId) => {
    try {
      setIsLoading(true);
      
      // Call BC service to activate user
      await bcService.updateUserStatus(userId, 'Active');
      
      // Update local state
      setUsers(users.map(user => 
        user.id === userId ? { ...user, status: 'Active' } : user
      ));
      
      // Close action menu
      setShowActionMenu(null);
    } catch (error) {
      logger.error('Error activating user:', error);
      setError('Failed to activate user. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle user deactivation
  const handleDeactivateUser = async (userId) => {
    try {
      setIsLoading(true);
      
      // Call BC service to deactivate user
      await bcService.updateUserStatus(userId, 'Inactive');
      
      // Update local state
      setUsers(users.map(user => 
        user.id === userId ? { ...user, status: 'Inactive' } : user
      ));
      
      // Close action menu
      setShowActionMenu(null);
    } catch (error) {
      logger.error('Error deactivating user:', error);
      setError('Failed to deactivate user. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Edit user modal component
  const EditUserModal = () => {
    const [formData, setFormData] = useState({
      name: editingUser?.name || '',
      email: editingUser?.email || '',
      role: editingUser?.role || 'User',
      isAdmin: editingUser?.isAdmin || false
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formError, setFormError] = useState(null);

    const handleChange = (e) => {
      const { name, value, type, checked } = e.target;
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
        ...(name === 'isAdmin' && checked ? { role: 'Admin' } : {})
      }));
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      const sanitizedName = formData.name.trim().replace(/<[^>]*>/g, '');
      const sanitizedEmail = formData.email.trim().toLowerCase();
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!sanitizedName || !sanitizedEmail) { setFormError('Name and email are required.'); return; }
      if (!emailRegex.test(sanitizedEmail)) { setFormError('Please enter a valid email address.'); return; }
      if (sanitizedName.length > 100) { setFormError('Name must be 100 characters or fewer.'); return; }
      const sanitizedData = { ...formData, name: sanitizedName, email: sanitizedEmail };
      try {
        setIsSubmitting(true);
        setFormError(null);
        const result = await bcService.updatePortalUser(editingUser.id, sanitizedData);
        setUsers(prev => prev.map(u => u.id === editingUser.id ? { ...u, ...formData, ...(result || {}) } : u));
        setShowEditModal(false);
        setEditingUser(null);
      } catch (error) {
        setFormError(error.message || 'Failed to update user. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    };

    return (
      <div className="fixed z-10 inset-0 overflow-y-auto">
        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <div className="fixed inset-0 transition-opacity" aria-hidden="true">
            <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
          </div>
          <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
          <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-indigo-100 sm:mx-0 sm:h-10 sm:w-10">
                  <UserCog className="h-6 w-6 text-indigo-600" />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">Edit User</h3>
                  <p className="mt-1 text-sm text-gray-500">{editingUser?.email}</p>
                </div>
              </div>

              {formError && (
                <div className="mt-4 p-2 bg-red-50 rounded-md">
                  <p className="text-sm text-red-700">{formError}</p>
                </div>
              )}

              <form className="mt-6" onSubmit={handleSubmit}>
                <div className="space-y-6">
                  <div>
                    <label htmlFor="edit-name" className="block text-sm font-medium text-gray-700">Full Name</label>
                    <div className="mt-1">
                      <input
                        type="text" id="edit-name" name="name"
                        value={formData.name} onChange={handleChange}
                        className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="edit-email" className="block text-sm font-medium text-gray-700">Email Address</label>
                    <div className="mt-1">
                      <input
                        type="email" id="edit-email" name="email"
                        value={formData.email} onChange={handleChange}
                        className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="edit-role" className="block text-sm font-medium text-gray-700">Role</label>
                    <select
                      id="edit-role" name="role"
                      value={formData.role} onChange={handleChange}
                      disabled={formData.isAdmin}
                      className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    >
                      <option value="User">User</option>
                      <option value="Admin">Admin</option>
                    </select>
                  </div>
                  <div className="mt-4">
                    <div className="flex items-center">
                      <input
                        id="edit-isAdmin" name="isAdmin" type="checkbox"
                        checked={formData.isAdmin} onChange={handleChange}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <label htmlFor="edit-isAdmin" className="ml-2 block text-sm text-gray-900">Administrator Access</label>
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      Administrators can manage users, environments, and authentication settings
                    </p>
                  </div>
                  <div className="mt-4 p-4 bg-amber-50 rounded-md border border-amber-200">
                    <div className="flex items-center">
                      <AlertTriangle className="h-5 w-5 text-amber-500 mr-2 flex-shrink-0" />
                      <p className="text-sm text-amber-700">
                        <strong>Security Notice:</strong> Administrator users have complete access to all portal features and can manage other users.
                      </p>
                    </div>
                  </div>
                </div>
              </form>
            </div>
            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="button" onClick={handleSubmit} disabled={isSubmitting}
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm"
              >
                {isSubmitting ? <><RefreshCw className="animate-spin -ml-1 mr-2 h-4 w-4" />Saving...</> : 'Save Changes'}
              </button>
              <button
                type="button" onClick={() => { setShowEditModal(false); setEditingUser(null); }}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Create new user component
  const CreateUserModal = () => {
    const [formData, setFormData] = useState({
      name: '',
      email: '',
      role: 'User',
      isAdmin: false
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formError, setFormError] = useState(null);
    
    const handleChange = (e) => {
      const { name, value, type, checked } = e.target;
      setFormData(prev => ({ 
        ...prev, 
        [name]: type === 'checkbox' ? checked : value,
        // Automatically set role to Admin if isAdmin is checked
        ...(name === 'isAdmin' && checked ? { role: 'Admin' } : {})
      }));
    };
    
    const handleSubmit = async (e) => {
      e.preventDefault();
      const sanitizedName = formData.name.trim().replace(/<[^>]*>/g, '');
      const sanitizedEmail = formData.email.trim().toLowerCase();
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!sanitizedName || !sanitizedEmail) {
        setFormError('Name and email are required fields.');
        return;
      }
      if (!emailRegex.test(sanitizedEmail)) { setFormError('Please enter a valid email address.'); return; }
      if (sanitizedName.length > 100) { setFormError('Name must be 100 characters or fewer.'); return; }
      const sanitizedData = { ...formData, name: sanitizedName, email: sanitizedEmail };

      try {
        setIsSubmitting(true);
        setFormError(null);
        
        // Call BC service to create user
        const result = await bcService.createPortalUser(sanitizedData);

        // Add new user to the list — default status to Active
        setUsers(prev => [...prev, { status: 'Active', ...result }]);

        // Close modal
        setShowCreateModal(false);
      } catch (error) {
        logger.error('Error creating user:', error);
        setFormError(error.message || 'Failed to create user. Please try again.');

        // For development, add mock user anyway
        if (process.env.NODE_ENV === 'development') {
          const newUser = {
            id: `new-${Date.now()}`,
            name: formData.name,
            email: formData.email,
            status: 'Active',
            role: formData.role,
            isAdmin: formData.isAdmin,
            lastLogin: null,
            created: new Date().toISOString()
          };

          setUsers(prev => [...prev, newUser]);
          setShowCreateModal(false);
        }
      } finally {
        setIsSubmitting(false);
      }
    };
    
    return (
      <div className="fixed z-10 inset-0 overflow-y-auto">
        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <div className="fixed inset-0 transition-opacity" aria-hidden="true">
            <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
          </div>
          
          <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
          
          <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-indigo-100 sm:mx-0 sm:h-10 sm:w-10">
                  <UserPlus className="h-6 w-6 text-indigo-600" />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">Add New User</h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      Create a new portal user account. The user will receive an email with login instructions.
                    </p>
                  </div>
                </div>
              </div>
              
              {formError && (
                <div className="mt-4 p-2 bg-red-50 rounded-md">
                  <p className="text-sm text-red-700">{formError}</p>
                </div>
              )}
              
              <form className="mt-6" onSubmit={handleSubmit}>
                <div className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                      Full Name
                    </label>
                    <div className="mt-1">
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                      Email Address
                    </label>
                    <div className="mt-1">
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label htmlFor="role" className="block text-sm font-medium text-gray-700">
                      Role
                    </label>
                    <select
                      id="role"
                      name="role"
                      value={formData.role}
                      onChange={handleChange}
                      className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      disabled={formData.isAdmin}
                    >
                      <option value="User">User</option>
                      <option value="Admin">Admin</option>
                    </select>
                  </div>
                  
                  <div className="mt-4">
                    <div className="flex items-center">
                      <input
                        id="isAdmin"
                        name="isAdmin"
                        type="checkbox"
                        checked={formData.isAdmin}
                        onChange={handleChange}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <label htmlFor="isAdmin" className="ml-2 block text-sm text-gray-900">
                        Administrator Access
                      </label>
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      Administrators can manage users, environments, and authentication settings
                    </p>
                  </div>
                  
                  <div className="mt-4 p-4 bg-amber-50 rounded-md border border-amber-200">
                    <div className="flex items-center">
                      <AlertTriangle className="h-5 w-5 text-amber-500 mr-2 flex-shrink-0" />
                      <p className="text-sm text-amber-700">
                        <strong>Security Notice:</strong> Administrator users have complete access to all portal features and can manage other users.
                      </p>
                    </div>
                  </div>
                </div>
              </form>
            </div>
            
            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm"
              >
                {isSubmitting ? (
                  <>
                    <RefreshCw className="animate-spin -ml-1 mr-2 h-4 w-4" />
                    Creating...
                  </>
                ) : 'Create User'}
              </button>
              <button
                type="button"
                onClick={() => setShowCreateModal(false)}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };
  
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };
  
  // Get status badge based on user status
  const getStatusBadge = (status) => {
    switch (status) {
      case 'Active':
        return (
          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
            Active
          </span>
        );
      case 'PendingFirstLogin':
        return (
          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
            Pending
          </span>
        );
      case 'Inactive':
        return (
          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
            Inactive
          </span>
        );
      default:
        return (
          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
            {status}
          </span>
        );
    }
  };
  
  if (isLoading && users.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }
  
  return (
    <div>
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between mb-6">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            User Management
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Manage employee access to the Business Central portal
          </p>
        </div>
        <div className="mt-4 flex md:mt-0 md:ml-4">
          <button
            type="button"
            onClick={() => setShowCreateModal(true)}
            className="ml-3 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <PlusCircle className="-ml-1 mr-2 h-5 w-5" />
            Add User
          </button>
        </div>
      </div>
      
      {/* Tabs */}
      <div className="bg-white shadow rounded-lg mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-1 px-4" aria-label="Tabs">
            {[
              { id: 'all', label: 'All Users', count: users.length },
              { id: 'active', label: 'Active', count: users.filter(u => u.status === 'Active').length },
              { id: 'inactive', label: 'Inactive', count: users.filter(u => u.status === 'Inactive' || u.status === 'PendingFirstLogin').length },
              { id: 'admin', label: 'Admins', count: users.filter(u => u.isAdmin).length },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`whitespace-nowrap py-4 px-4 border-b-2 font-medium text-sm flex items-center gap-2 ${
                  activeTab === tab.id
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                  activeTab === tab.id ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-600'
                }`}>
                  {tab.count}
                </span>
              </button>
            ))}
          </nav>
        </div>

        {/* Search */}
        <div className="p-4">
          <div className="relative rounded-md shadow-sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
              placeholder="Search by name or email"
            />
          </div>
        </div>
      </div>
      
      {/* Error message */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <XCircle className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}
      
      {/* User list */}
      <div className="bg-white shadow sm:rounded-md">
        <ul className="divide-y divide-gray-200">
          {filteredUsers.length === 0 ? (
            <li className="px-6 py-10 text-center">
              <p className="text-gray-500">No users found matching your criteria.</p>
              <button
                onClick={() => {
                  setSearchTerm('');
                  setActiveTab('all');
                }}
                className="mt-2 text-sm text-indigo-600 hover:text-indigo-500"
              >
                Clear filters
              </button>
            </li>
          ) : (
            filteredUsers.map((user, index) => {
              const isNearBottom = index >= filteredUsers.length - 2;
              return (
              <li key={user.id} className="px-0 py-0">
                <div className="flex items-center px-6 py-4 hover:bg-gray-50">
                  <div className="min-w-0 flex-1 flex items-center">
                    <div className="flex-shrink-0">
                      <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center">
                        <span className="text-lg font-medium text-indigo-800">
                          {user.name.charAt(0)}
                        </span>
                      </div>
                    </div>
                    <div className="min-w-0 flex-1 px-4">
                      <div>
                        <p className="text-sm font-medium text-indigo-600 truncate">{user.name}</p>
                        <p className="mt-1 flex items-center text-sm text-gray-500">
                          <span className="truncate">{user.email}</span>
                        </p>
                      </div>
                      <div className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0">
                        <p className="text-sm text-gray-500">
                          Last login: {formatDate(user.lastLogin)}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    {/* Status badge */}
                    <div>{getStatusBadge(user.status)}</div>
                    
                    {/* Admin badge if applicable */}
                    {user.isAdmin && (
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800">
                        Admin
                      </span>
                    )}
                    
                    {/* Actions dropdown */}
                    <div className="relative inline-block text-left">
                      <div>
                        <button
                          type="button"
                          className="bg-white rounded-md p-2 inline-flex items-center justify-center text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500"
                          id={`options-menu-${user.id}`}
                          aria-expanded="true"
                          aria-haspopup="true"
                          onClick={() => toggleActionMenu(user.id)}
                        >
                          <span className="sr-only">Open options</span>
                          <MoreHorizontal className="h-5 w-5" />
                        </button>
                      </div>
                      
                      {showActionMenu === user.id && (
                        <div
                          className={`absolute right-0 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50 ${
                            isNearBottom ? 'bottom-10 origin-bottom-right' : 'top-full mt-1 origin-top-right'
                          }`}
                          role="menu"
                          aria-orientation="vertical"
                          aria-labelledby={`options-menu-${user.id}`}
                        >
                          {user.isAdmin ? (
                            <div className="py-3 px-4 text-sm text-gray-500 flex items-center gap-2">
                              <Shield className="h-4 w-4 text-purple-500 flex-shrink-0" />
                              Admin users cannot be modified
                            </div>
                          ) : (
                            <div className="py-1" role="none">
                              {user.status !== 'Active' && (
                                <button
                                  onClick={() => handleActivateUser(user.id)}
                                  className="flex items-center w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                  role="menuitem"
                                >
                                  <CheckCircle className="mr-3 h-5 w-5 text-green-500" />
                                  Activate User
                                </button>
                              )}
                              {user.status === 'Active' && (
                                <button
                                  onClick={() => handleDeactivateUser(user.id)}
                                  className="flex items-center w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                  role="menuitem"
                                >
                                  <XCircle className="mr-3 h-5 w-5 text-red-500" />
                                  Deactivate User
                                </button>
                              )}
                              <button
                                onClick={() => { setEditingUser(user); setShowEditModal(true); setShowActionMenu(null); }}
                                className="flex items-center w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                role="menuitem"
                              >
                                <Edit className="mr-3 h-5 w-5 text-gray-500" />
                                Edit User
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </li>
              );
            })
          )}
        </ul>
      </div>
      
      {/* Edit User Modal */}
      {showEditModal && editingUser && <EditUserModal />}

      {/* Create User Modal */}
      {showCreateModal && <CreateUserModal />}
    </div>
  );
};

export default UserManagement;